import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  Link,
  Stack,
  Toolbar,
} from '@mui/material';
//
import useOffSetTop from '../../hooks/useOffSetTop';
import useResponsive from '../../hooks/useResponsive';
import { useAuth } from '../../contexts/AuthContext';
import Iconify from '../../components/Iconify';
import { PATH_AUTH, PATH_DASHBOARD, PATH_PAGE } from '../../routes/paths';
import { NAV_ITEMS } from './navConfig';
import AccountMenu from './AccountMenu';
import BrandLockup from '../../components/BrandLockup';
import MobileNavigation from './MobileNavigation';

// ----------------------------------------------------------------------

const HEADER_HEIGHT = { xs: 72, md: 82 };

export default function MainHeader() {
  const { pathname } = useLocation();
  const isDesktop = useResponsive('up', 'lg');
  const isScrolled = useOffSetTop(8);
  const { isAuthenticated, isEmployer, isAdmin } = useAuth();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Close the drawer whenever navigation happens.
  useEffect(() => {
    setIsDrawerOpen(false);
  }, [pathname, isDesktop]);

  const dashboardPath = isAdmin
    ? PATH_DASHBOARD.admin.root
    : isEmployer
      ? PATH_DASHBOARD.employer.root
      : PATH_DASHBOARD.employee.root;

  const isActive = (path: string) =>
    path === PATH_PAGE.root ? pathname === path : pathname.startsWith(path);

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: '#F8F8FDF5',
          backdropFilter: 'blur(16px)',
          color: 'text.primary',
          borderBottom: 'none',
          borderColor: 'divider',
          boxShadow: isScrolled ? (theme) => theme.customShadows.card : 'none',
          transition: 'box-shadow .2s',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ height: HEADER_HEIGHT, gap: { xs: 0.75, sm: 2 } }}>
            <Box
              component={RouterLink}
              to={PATH_PAGE.root}
              sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, textDecoration: 'none' }}
              aria-label="Job Victory home"
            >
              <BrandLockup />
            </Box>

            {isDesktop && (
              <Stack
                component="nav"
                aria-label="Main navigation"
                direction="row"
                spacing={3}
                sx={{ ml: 'auto' }}
              >
                {NAV_ITEMS.map((item) => (
                  <Link
                    key={item.path}
                    component={RouterLink}
                    to={item.path}
                    underline="none"
                    variant="subtitle2"
                    aria-current={isActive(item.path) ? 'page' : undefined}
                    sx={{
                      fontSize: 14,
                      whiteSpace: 'nowrap',
                      py: 1,
                      color: isActive(item.path) ? 'primary.main' : 'text.primary',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    {item.title}
                  </Link>
                ))}
              </Stack>
            )}

            <Box sx={{ flexGrow: 1 }} />

            {isAuthenticated ? (
              <Stack direction="row" spacing={1.5} alignItems="center">
                {isDesktop && (
                  <Button
                    component={RouterLink}
                    to={dashboardPath}
                    variant="outlined"
                    color="inherit"
                  >
                    Dashboard
                  </Button>
                )}
                <AccountMenu />
              </Stack>
            ) : (
              isDesktop && (
                <Stack direction="row" spacing={1.5}>
                  <Button component={RouterLink} to={PATH_AUTH.login} color="primary">
                    Login
                  </Button>
                  <Divider orientation="vertical" flexItem />
                  <Button component={RouterLink} to={PATH_AUTH.signup} variant="contained">
                    Sign up
                  </Button>
                </Stack>
              )
            )}

            {!isDesktop && (
              <IconButton
                onClick={() => setIsDrawerOpen(true)}
                aria-label="Open menu"
                aria-expanded={isDrawerOpen}
                aria-controls={isDrawerOpen ? 'mobile-navigation' : undefined}
                sx={{ color: 'text.primary' }}
              >
                <Iconify icon="eva:menu-2-fill" width={24} height={24} />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <MobileNavigation
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        dashboardPath={dashboardPath}
      />
    </>
  );
}
